import { SortDirections, wafflesKeyColumnName } from "@app/shared/waffles";
import { ProcessingSpecialtyGroupExceptionsPayload, ZipCodeAnalysisStatusChangeNotificationMessagePayload, ZipCodeAnalysisNcpStatusChangeNotificationMessagePayload } from "@app/notifications/message";
import ProjectRunConfiguration from "@app/projects/projectRunConfiguration";
import Project from "@app/projects/project";
import { JSX } from "react";
import { ChangedGapsEnum, NetworkAssessmentAnalysisType, ProjectionProviderType } from "@app/projects/enumerators";
import { ApplyFiltersAction } from "@app/projects/zip-projects/interfaces/actions/applyFiltersAction";
import { ChangeChangedGapsFilterAction } from "@app/projects/zip-projects/interfaces/actions/changeChangedGapsFilterAction";
import { ChangeExcludeZeroMembershipAction } from "@app/projects/zip-projects/interfaces/actions/changeExcludeZeroMembershipAction";
import { ChangeCheckboxAction } from "@app/projects/zip-projects/interfaces/actions/changeCheckboxAction";
import { ChangeNcpProjectRunStatus } from "@app/projects/zip-projects/interfaces/actions/changeNcpProjectRunStatusAction";
import { ZipContextState } from "@app/projects/zip-projects/interfaces/zipContextState";
import { ZipAnalysisSearchSelections } from "@app/projects/zip-projects/interfaces/zipAnalysisSearchSelections";
import { ZipAnalysisSearchParameters, ZipClassAnalysisSearchParameters, ZipCodeAnalysisSearchParameters } from "@app/projects/zip-projects/interfaces/zipAnalysisSearchParameters";
import { ZipAnalysisSortOption } from "@app/projects/zip-projects/interfaces/zipAnalysisSortOption";
import { ZipCodeDisplayModel } from "@app/projects/zip-projects/interfaces/zipCodeDisplayModel";
import { ZipClassAggregateResultDisplayModel } from "@app/projects/zip-projects/interfaces/zipClassAggregateResultDisplayModel";
import { ChangeCompareToProjectRunConfigurationAction } from "@app/projects/zip-projects/interfaces/actions/changeCompareToRunConfigurationAction";
import { ChangeFilterAction } from "@app/projects/zip-projects/interfaces/actions/changeFilterAction";
import { ChangePageAction } from "@app/projects/zip-projects/interfaces/actions/changePageAction";
import { ChangeMembershipsAction } from "@app/projects/zip-projects/interfaces/actions/changeMembershipsAction";
import { ChangePerspectiveAction } from "@app/projects/zip-projects/interfaces/actions/changePerspectiveAction";
import { ChangeProjectStatusAction } from "@app/projects/zip-projects/interfaces/actions/changeProjectStatusAction";
import { ChangeProjectRunConfigurationAction } from "@app/projects/zip-projects/interfaces/actions/changeProjectRunConfigurationAction";
import { ClickSpecialtyGroupOrZipCodeAction } from "@app/projects/zip-projects/interfaces/actions/clickSpecialtyGroupOrZipCodeAction";
import { EditProjectAction } from "@app/projects/zip-projects/interfaces/actions/editProjectAction";
import { EditTimeslicePublishCommentAction } from "@app/projects/zip-projects/interfaces/actions/editTimeslicePublishCommentAction";
import { ErrorAction } from "@app/projects/zip-projects/interfaces/actions/errorAction";
import { LoadingAction } from "@app/projects/zip-projects/interfaces/actions/loadingAction";
import { LoadProjectWithoutRunConfigurationAction } from "@app/projects/zip-projects/interfaces/actions/loadProjectWithoutRunConfigurationAction";
import { PublishProjectRunConfigurationAction } from "@app/projects/zip-projects/interfaces/actions/publishProjectRunConfigurationAction";
import { NavigateHistoryAction } from "@app/projects/zip-projects/interfaces/actions/navigateHistoryAction";
import { RenderedNetworkProvidersAction } from "@app/projects/zip-projects/interfaces/actions/renderedNetworkProvidersAction";
import { RenderedMarketProvidersAction } from "@app/projects/zip-projects/interfaces/actions/renderedMarketProvidersAction";
import { RenderedProspectProvidersAction } from "@app/projects/zip-projects/interfaces/actions/renderedProspectProvidersAction";
import { ResetFiltersAction } from "@app/projects/zip-projects/interfaces/actions/resetFiltersAction";
import { SortTableAction } from "@app/projects/zip-projects/interfaces/actions/sortTableAction";
import { StartRunProjectAction } from "@app/projects/zip-projects/interfaces/actions/startRunProjectAction";
import { ProcessProviderChangesAction } from "@app/projects/zip-projects/interfaces/actions/processProviderChangesAction";
import { UpdatedUnprocessedProvidersAction } from "@app/projects/zip-projects/interfaces/actions/updatedUnprocessedProvidersAction";
import { SubmitSpecialtyExceptionAction } from "@app/projects/zip-projects/interfaces/actions/submitSpecialtyExceptionAction";
import { FinishProcessingExceptionAction } from "@app/projects/zip-projects/interfaces/actions/finishProcessingExceptionAction";
import { StartProcessingExceptionAction } from "@app/projects/zip-projects/interfaces/actions/startProcessingExceptionAction";
import { SpecialtyGroupException } from "@app/exceptions/interfaces";
import { UserInfo } from "@app/user/UserContext";
import { ServiceAreaNestingParentDropdownItem } from "@app/report-customization/manage-presets/interfaces/serviceAreaNestingParentDropdownItem";
import { LoadedFeatureFlagsAction } from "@app/projects/zip-projects/interfaces/actions/loadedFeatureFlagsAction";
import { DropdownItem } from "@app/shared/checkbox-dropdown/types";

export interface ZipContextInProps {
  user: UserInfo,
  children: React.ReactNode;
}

export interface ZipContextEventHandlers {
  onChangeChangedGapsFilter: (newChangedGapsFilter: ChangedGapsEnum) => void;
  onChangeCompareToRunConfiguration: (newCompareToRunConfigurationId: number) => void;
  onChangeExcludeZeroMembership: (excludeZeroMembership: boolean) => void;
  onChangePerspectiveToZipClass: (project?: Project, runConfiguration?: ProjectRunConfiguration) => void;
  onChangePerspectiveToZipCode: (project?: Project, runConfiguration?: ProjectRunConfiguration) => void;
  onChangeProjectStatus: (payload: ZipCodeAnalysisStatusChangeNotificationMessagePayload) => void;
  onChangeRunConfiguration: (newRunConfigurationId: number) => void;
  onChangeSelectedMemberships: (newMemberships: DropdownItem<string>[]) => void;
  onEditTimeslicePublishComment: () => void;
  onFinishProcessingException: (newProject: Project, newRunConfiguration: ProjectRunConfiguration, shouldRefreshExceptions: boolean) => void;
  onLoadPage: (projectId: string) => void;
  onApplyFilters: () => void;
  onResetFilters: () => void;
  onRunProject: (projectId: number) => void;
  onProcessProviderChanges: (reprocessFailure: boolean) => Promise<void>;
  onProcessSpecialtyExceptions: (newProject: Project, newRunConfiguration: ProjectRunConfiguration, notificationPayload?: ProcessingSpecialtyGroupExceptionsPayload) => void;
  onPublishTimeSlice: (comment?: string) => Promise<void>;
  onSubmitEditProject: (projectId: number) => void;
  onChangeTablePage: (newSkip: number) => void;
  onSortTable: (column: keyof (ZipCodeTableData & ZipClassTableData), direction: SortDirections) => void;
  onSubmitSpecialtyException: (exception: SpecialtyGroupException) => void;
  onClickSpecialtyGroupOrZipCode: (params: ZipCodeFilterParams) => void;
  /** Pass projectionType when the caller knows whether targets or exclusions changed, so only that category's flag moves. */
  onUpdateUnprocessedProjectionProviders: (hasUnprocessed: boolean, projectionType?: ProjectionProviderType) => void;
  onUpdateUnprocessedProviders: (hasUnprocessedTerminations: boolean, hasUnprocessedTargets: boolean, hasUnprocessedExcludes?: boolean) => void;
  onUpdateUnprocessedTerminations: (hasUnprocessed: boolean) => void;
  onNcpProjectRunStatusChange: (payload: ZipCodeAnalysisNcpStatusChangeNotificationMessagePayload) => void;
}

export interface ZipContextEventHandlersObject {
  zipContextEvents: ZipContextEventHandlers;
}

export interface ZipContextOutProps extends ZipContextEventHandlers {
  dispatch: React.Dispatch<ZipProjectAction>;
  getLatestRunConfiguration: (project: Project) => ProjectRunConfiguration;
  getSearchParametersFromFilterSelections: (filterSelections: ZipAnalysisSearchSelections) => ZipAnalysisSearchParameters;
  state: ZipContextState;
}

/// Used when constructing filter params for clicking specialty group or zip code links
export interface ZipCodeFilterParams {
  county?: string;
  specialtyGroupCode: string;
  stateName?: string;
  zipCode: string;
}

export enum ZipProjectActionsEnum {
  // user-driven actions
  ChangeChangedGapsFilter = "ChangeChangedGapsFilter",
  ChangeCheckbox = "ChangeCheckbox",
  ChangeFilter = "ChangeFilter",
  ChangeMemberships = "ChangeMemberships",
  ChangePage = "ChangePage",
  ChangePerspectiveZipClass = "ChangePerspectiveZipClass",
  ChangePerspectiveZipCode = "ChangePerspectiveZipCode",
  ChangeCompareToProjectRunConfiguration = "ChangeCompareToProjectRunConfiguration",
  ChangeProjectRunConfiguration = "ChangeProjectRunConfiguration",
  ChangeExcludeZeroMembership = "ChangeExcludeZeroMembership",
  ClickSpecialtyGroupOrZipCode = "ClickZipOrSpecialtyGroupCode",
  EditProject = "EditProject",
  EditTimeslicePublishComment = "EditTimeslicePublishComment",
  EndRunProject = "EndRunProject",
  Error = "Error",
  Loading = "Loading",
  LoadProjectWithoutRunConfiguration = "LoadProjectWithoutRunConfiguration",
  NavigateHistory = "NavigateHistory",
  ProcessProviderChanges = "ProcessProviderChanges",
  PublishProjectRunConfiguration = "PublishProjectRunConfiguration",
  RenderedMarketProviders = "RenderedMarketProviders",
  RenderedNetworkProviders = "RenderedNetworkProviders",
  RenderedProspectProviders = "RenderedProspectProviders",
  ResetFilters = "ResetFilters",
  SortTable = "SortTable",
  StartRunProject = "StartRunProject",
  SubmitSpecialtyExceptions = "SubmitSpecialtyException",
  UpdatedUnprocessedProviders = "UpdatedUnprocessedProviders",
  ApplyFilters = "SubmitFilters",

  // these are triggered by signalr notifications, not the user
  ChangeProjectStatus = "ChangeProjectStatus",
  ChangeNcpProjectRunStatus = "ChangeNcpProjectRunStatus",
  StartProcessingException = "StartProcessingException",
  FinishProcessingException = "FinishProcessingException",

  // this is triggered by an external async api call
  LoadedFeatureFlags = "LoadedFeatureFlags",
}

export type ZipProjectAction = {
  type: ZipProjectActionsEnum.ApplyFilters,
  payload: ApplyFiltersAction,
} | {
  type: ZipProjectActionsEnum.ChangeChangedGapsFilter,
  payload: ChangeChangedGapsFilterAction,
} | {
  type: ZipProjectActionsEnum.ChangeExcludeZeroMembership,
  payload: ChangeExcludeZeroMembershipAction,
} | {
  type: ZipProjectActionsEnum.ChangeCheckbox,
  payload: ChangeCheckboxAction,
} | {
  type: ZipProjectActionsEnum.ChangeCompareToProjectRunConfiguration,
  payload: ChangeCompareToProjectRunConfigurationAction,
} | {
  type: ZipProjectActionsEnum.ChangeFilter,
  payload: ChangeFilterAction,
} | {
  type: ZipProjectActionsEnum.ChangePage,
  payload: ChangePageAction,
} | {
  type: ZipProjectActionsEnum.ChangeMemberships,
  payload: ChangeMembershipsAction,
} | {
  type: ZipProjectActionsEnum.ChangePerspectiveZipClass,
  payload: ChangePerspectiveAction,
} | {
  type: ZipProjectActionsEnum.ChangePerspectiveZipCode,
  payload: ChangePerspectiveAction,
} | {
  type: ZipProjectActionsEnum.ChangeProjectStatus,
  payload: ChangeProjectStatusAction,
} | {
  type: ZipProjectActionsEnum.ChangeProjectRunConfiguration,
  payload: ChangeProjectRunConfigurationAction,
} | {
  type: ZipProjectActionsEnum.ClickSpecialtyGroupOrZipCode,
  payload: ClickSpecialtyGroupOrZipCodeAction,
} | {
  type: ZipProjectActionsEnum.EditProject,
  payload: EditProjectAction,
} | {
  type: ZipProjectActionsEnum.EditTimeslicePublishComment,
  payload: EditTimeslicePublishCommentAction,
} | {
  type: ZipProjectActionsEnum.Error,
  payload: ErrorAction,
} | {
  type: ZipProjectActionsEnum.FinishProcessingException,
  payload: FinishProcessingExceptionAction,
} | {
  type: ZipProjectActionsEnum.LoadedFeatureFlags,
  payload: LoadedFeatureFlagsAction,
} | {
  type: ZipProjectActionsEnum.Loading,
  payload: LoadingAction,
} | {
  type: ZipProjectActionsEnum.LoadProjectWithoutRunConfiguration,
  payload: LoadProjectWithoutRunConfigurationAction,
} | {
  type: ZipProjectActionsEnum.ChangeNcpProjectRunStatus,
  payload: ChangeNcpProjectRunStatus
} | {
  type: ZipProjectActionsEnum.PublishProjectRunConfiguration,
  payload: PublishProjectRunConfigurationAction,
} | {
  type: ZipProjectActionsEnum.NavigateHistory,
  payload: NavigateHistoryAction,
} | {
  type: ZipProjectActionsEnum.RenderedNetworkProviders,
  payload: RenderedNetworkProvidersAction,
} | {
  type: ZipProjectActionsEnum.RenderedMarketProviders,
  payload: RenderedMarketProvidersAction,
} | {
  type: ZipProjectActionsEnum.RenderedProspectProviders,
  payload: RenderedProspectProvidersAction,
} | {
  type: ZipProjectActionsEnum.ResetFilters,
  payload: ResetFiltersAction,
} | {
  type: ZipProjectActionsEnum.SortTable,
  payload: SortTableAction,
} | {
  type: ZipProjectActionsEnum.StartRunProject,
  payload: StartRunProjectAction,
} | {
  type: ZipProjectActionsEnum.ProcessProviderChanges,
  payload: ProcessProviderChangesAction,
} | {
  type: ZipProjectActionsEnum.StartProcessingException,
  payload: StartProcessingExceptionAction,
} | {
  type: ZipProjectActionsEnum.SubmitSpecialtyExceptions,
  payload: SubmitSpecialtyExceptionAction,
} | {
  type: ZipProjectActionsEnum.UpdatedUnprocessedProviders,
  payload: UpdatedUnprocessedProvidersAction,
};

export interface ZipProjectRouteComponentProps {
  projectId: string;
}

/**
 * The model used to retrieve zip analysis results for an entire project,
 * as opposed to a single regionSetSpecialtyGroup.
 * The run configuration ids are both only for compare mode.
 */
export interface ZipAnalysisQueryParameters {
  limit?: number;
  offset?: number;
  searchParameters: ZipCodeAnalysisSearchParameters | ZipClassAnalysisSearchParameters;
  sortOptions: ZipAnalysisSortOption[];
  excludeZeroMembership?: boolean;
  networkAssessmentAnalysisType?: NetworkAssessmentAnalysisType;
}

export interface ComparingZipAnalysisQueryParameters {
  projectRunConfigurationId1: number;
  projectRunConfigurationId2: number;
  parameters: ZipAnalysisQueryParameters;
  changedGaps: ChangedGapsEnum;
  getGaps: boolean;
}

export interface SpecialtyGroupDisplay {
  specialtyGroupCode: string;
  specialtyGroupDescription: string;
}

export interface ZipCodeAnalysisSearchOptionsResults {
  filters: {
    stateNames: string[];
    counties: string[];
    zipClasses: string[];
    specialtyGroupCodes: SpecialtyGroupDisplay[];
    zipCodes: string[];
    cities: string[];
    metPassPercent: string[];
    countiesAndStates: ServiceAreaNestingParentDropdownItem[];
  };
  projectRunConfigurationId: number;
}

export interface ZipClassAnalysisSearchOptionsResults {
  filters: {
    zipClasses: string[];
    specialtyGroupCodes: SpecialtyGroupDisplay[];
    metOverallOptions: string[];
  };
  projectRunConfigurationId: number;
}

export interface SpecialtyLevelResults {
  [key: string]: ZipCodeDisplayModel[] | ZipClassAggregateResultDisplayModel[];
}

export interface ZipDetailsApiResponse {
  data: ZipCodeDisplayModel[] | ZipClassAggregateResultDisplayModel[];
  hasExpandableSpecialties: boolean;
  projectRunConfigurationId: number;
  resultsFor: string;
  specialtyLevelResults: SpecialtyLevelResults;
  totalNumberOfRecords: number;
}

/**
 * This is the data actually rendered in the table, after making some updates
 * to the ZipAnalysisDisplayModel fields. Adding hyperlinks, formatting numbers, etc.
 * These keys are in the display order of the table columns.
 */
export interface ZipCodeTableData {
  // this will be the region set specialty group id
  [wafflesKeyColumnName]: string;
  state: string;
  county: string;
  city: string;
  zipClass: string;
  zipCode: JSX.Element;
  isInformationalSpecialty: boolean;
  specialty: JSX.Element | string;
  membershipType: string;
  resultsFor: string;
  minProviderCount: string;
  reqDist: string;
  reqTime: string;
  minPassPercent: string;
  metDist: string;
  metTime: string;
  metPassPercent: string;
  gapAgeDays: string;
  membershipCount: string;
  accessTelehealthCredit: string;
  providerCount: string;
  inZipProviderCount: string;
  memberCountWithAccess: string;
  withAccessPercent: string;
  memberCountWithoutAccess: string;
  withoutAccessPercent: string;
  avgDist: string;
  avgDist2: string;
  avgDistWith: string;
  avgDistWithout: string;
  maxDist: string;
  maxDist2: string;
  maxDistWith: string;
  maxDistWithout: string;
  withDistPercent: string;
  avgTime: string;
  avgTime2: string;
  avgTimeWith: string;
  avgTimeWithout: string;
  maxTime: string;
  maxTime2: string;
  maxTimeWith: string;
  maxTimeWithout: string;
  withTimePercent: string;
  avgDistWith2: string;
  avgDistWithout2: string;
  maxDistWith2: string;
  maxDistWithout2: string;
  avgTimeWith2: string;
  avgTimeWithout2: string;
  maxTimeWith2: string;
  maxTimeWithout2: string;
  marketPercent: string;
  marketAccessPercent: string;
  marketServicing: string;
  inZipMarketProviderCount: string;
  servicingProviderToMemberRatio: string;
  gapFillStatus: string;
  gapFillStatusUpdatedDate: string;
  gapFillStatusUpdatedByName: string;
}

export enum ZipProjectModuleRoutesEnum {
  Adequacy = "adequacy",
  Mapping = "mapping",
  NetworkIntegrity = "network-integrity",
  ProviderHub = "provider-hub",
  Summary = "summary",
}

/**
 * This is the transformed zip class data displayed in the table.
 */
export interface ZipClassTableData {
  [wafflesKeyColumnName]: string;
  zipClass: string;
  isInformationalSpecialty: boolean;
  specialty: JSX.Element | string;
  membershipType: string;
  resultsFor: string;
  minProviderCount: string;
  reqDist: string;
  reqTime: string;
  minPassPercent: string;
  membershipCount: string;
  accessTelehealthCredit: string;
  providerCount: string;
  inClassProviderCount: string;
  memberCountWith: string;
  withPercent: string;
  memberCountWithout: string;
  withoutPercent: string;
  metOverall: string;
  avgDist: string;
  avgDist2: string;
  avgDistWith: string;
  avgDistWithout: string;
  exceptions: string;
  maxDist: string;
  maxDist2: string;
  maxDistWith: string;
  maxDistWithout: string;
  withDistPercent: string;
  avgTime: string;
  avgTime2: string;
  avgTimeWith: string;
  avgTimeWithout: string;
  maxTime: string;
  maxTime2: string;
  maxTimeWith: string;
  maxTimeWithout: string;
  withTimePercent: string;
  avgDistWith2: string;
  avgDistWithout2: string;
  maxDistWith2: string;
  maxDistWithout2: string;
  avgTimeWith2: string;
  avgTimeWithout2: string;
  maxTimeWith2: string;
  maxTimeWithout2: string;
  metDist: string;
  metTime: string;
  marketPercent: string;
  marketAccessPercent: string;
  marketServicing: string;
  inClassMarketProviderCount: string;
  servicingProviderToMemberRatio: string
}
